import React, { useRef, useState } from "react";
import { Upload, Loader, FileText as FileIcon } from "lucide-react";
import DocumentGrid from "./DocumentGrid";
import DocumentQuotaDisplay from "./DocumentQuotaDisplay";
import useDocumentCompression from "../../../hooks/useDocumentCompression";
import ErrorMessage from "../common/ErrorMessage";

/**
 * Modern high-contrast document upload component with fallback quota & drag-and-drop
 */
const DocumentUpload = ({
  documents = [],
  onDocumentsChange,
  documentQuota,
  selectedDate,
}) => {
  const fileInputRef = useRef(null);
  const { isCompressing, compressionProgress, compressMultipleDocuments } = useDocumentCompression();
  const [error, setError] = useState(null);

  const activeQuota = documentQuota || {
    used: documents.length,
    total: 1,
    remaining: Math.max(0, 1 - documents.length),
  };

  const canUploadMore = activeQuota.remaining > 0;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setError(null);

    const totalAfterUpload = documents.length + files.length;

    if (totalAfterUpload > activeQuota.remaining + documents.length) {
      setError(
        `Document limit exceeded. You can only upload 1 document per day. You selected ${files.length} document${files.length === 1 ? "" : "s"}.`
      );
      e.target.value = "";
      return;
    }

    const validExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"];
    const invalidFiles = files.filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      return !validExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      setError(
        `Invalid file type(s). Allowed formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT. ` +
          `Invalid file(s): ${invalidFiles.map((f) => f.name).join(", ")}`
      );
      e.target.value = "";
      return;
    }

    const oversizedFiles = files.filter((file) => file.size > 30 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError(
        `File(s) exceed 30MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`
      );
      e.target.value = "";
      return;
    }

    try {
      const compressedDocuments = await compressMultipleDocuments(files, 30);
      const updatedDocuments = [...documents, ...compressedDocuments];
      onDocumentsChange(updatedDocuments);
      e.target.value = "";
    } catch (err) {
      console.error("Error processing documents:", err);
      setError("Failed to process documents. Please try again.");
    }
  };

  const handleRemoveDocument = (index) => {
    const updatedDocuments = documents.filter((_, i) => i !== index);
    onDocumentsChange(updatedDocuments);
  };

  return (
    <div className="space-y-6 font-sans">
      {/* Quota Display Header */}
      <DocumentQuotaDisplay quota={activeQuota} selectedDate={selectedDate} />

      {/* Compression Progress Indicator */}
      {isCompressing && compressionProgress && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3">
          <Loader className="w-5 h-5 text-indigo-600 animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-indigo-900">
              Compressing document...
            </p>
            {compressionProgress.current && compressionProgress.total && (
              <p className="text-xs text-indigo-700 mt-0.5 truncate">
                Processing {compressionProgress.current} of {compressionProgress.total}: {compressionProgress.fileName}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Upload Dropzone Box */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={isCompressing || !canUploadMore}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isCompressing || !canUploadMore}
          className={`
            w-full flex flex-col items-center justify-center p-8 sm:p-10
            border-2 border-dashed rounded-xl transition-all duration-200 group text-center
            ${
              isCompressing || !canUploadMore
                ? "border-slate-200 bg-slate-50 cursor-not-allowed text-slate-400"
                : "border-slate-300 bg-slate-50/50 hover:bg-slate-100/60 hover:border-slate-900 cursor-pointer"
            }
          `}
        >
          {isCompressing ? (
            <>
              <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-3" />
              <p className="text-sm font-bold text-slate-900">
                Optimizing Document...
              </p>
            </>
          ) : !canUploadMore ? (
            <>
              <FileIcon className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-500">
                Daily Document Quota Reached
              </p>
              <p className="text-xs text-slate-400 mt-1">
                You have reached the maximum limit of {activeQuota.total} document per day.
              </p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-3 shadow-sm group-hover:scale-105 transition-transform">
                <Upload className="w-6 h-6" />
              </div>
              <p className="text-base font-bold text-slate-900">
                Click to Upload Document
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Attach 1 document (PDF, Word, Excel, PowerPoint, Text)
              </p>
              <span className="inline-block mt-3 text-[11px] font-semibold text-slate-600 bg-white border border-slate-200 px-3 py-1 rounded-full shadow-xs">
                Supports PDF, DOCX, TXT • Max 30MB • Auto-compressed
              </span>
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <ErrorMessage
          message={error}
          type="error"
          onDismiss={() => setError(null)}
        />
      )}

      {/* Document Grid Preview */}
      {documents.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
          <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Attached Documents ({documents.length})
          </h5>
          <DocumentGrid documents={documents} onRemove={handleRemoveDocument} />
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
